import { useContext } from 'react';
import { CollapsibleMenuRow, Toggle } from '@/shared/components/export';
import { WidgetContainer, WidgetContainerContext } from '@/core/widgets/export';
import { qCode } from '@/modules/articles/model/concept-item';
import { VisibleGroup } from '@/modules/articles/components/scrollers/visible-group';

export interface AdvancedFilterPreferences {
  excludedItemsByGroup?: Record<string, string[]>;
  activeRequiredIncludes?: Record<string, boolean>;
}

/** Collapsible category rows (with a selected-count badge) for a widget's `FilterGroup`s — the inline replacement for the old FiltersOffcanvas modal. */
export default function AdvancedFilterCategories({
  visibleGroups,
  disabled,
}: {
  visibleGroups: VisibleGroup[];
  disabled?: boolean;
}) {
  const widgetContainer = useContext<WidgetContainer>(WidgetContainerContext);
  const preferences = widgetContainer.getPreferences<AdvancedFilterPreferences>();
  const excludedItemsByGroup = preferences?.excludedItemsByGroup ?? {};
  const activeRequiredIncludes = preferences?.activeRequiredIncludes ?? {};

  const toggleItem = (group: string, code: string, exclude: boolean, groupSize: number) => {
    if (disabled) return;
    const current = new Set(excludedItemsByGroup[group] || []);
    if (exclude && current.size + 1 === groupSize) return;
    if (exclude) current.add(code);
    else current.delete(code);
    widgetContainer.updatePreferences<AdvancedFilterPreferences>({
      excludedItemsByGroup: { ...excludedItemsByGroup, [group]: Array.from(current) },
    });
  };

  const toggleRequiredInclude = (group: string, active: boolean) => {
    if (disabled) return;
    widgetContainer.updatePreferences<AdvancedFilterPreferences>({
      activeRequiredIncludes: { ...activeRequiredIncludes, [group]: active },
    });
  };

  return (
    <>
      {visibleGroups.map(({ title: group, items, requiredInclude }) => {
        const excludedSet = new Set(excludedItemsByGroup[group] || []);
        const riActive = requiredInclude ? !!activeRequiredIncludes[group] : false;
        const selectedCount = riActive ? items.length : items.length - excludedSet.size;

        return (
          <CollapsibleMenuRow key={group} label={group} badge={selectedCount}>
            {requiredInclude && (
              <div
                role={'button'}
                tabIndex={disabled ? undefined : 0}
                className={`menu-list-item${disabled ? ' menu-list-item-disabled' : ''}`}
                onClick={() => toggleRequiredInclude(group, !riActive)}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleRequiredInclude(group, !riActive);
                  }
                }}
              >
                <span className={'menu-list-item-label'}>{requiredInclude.title}</span>
                <Toggle checked={riActive} disabled={disabled} />
              </div>
            )}
            {items.map(item => {
              const qc = qCode(item);
              const checked = riActive || !excludedSet.has(qc);
              const rowDisabled = disabled || riActive;

              return (
                <div
                  key={qc}
                  role={'button'}
                  tabIndex={rowDisabled ? undefined : 0}
                  className={`menu-list-item${rowDisabled ? ' menu-list-item-disabled' : ''}`}
                  onClick={() => !rowDisabled && toggleItem(group, qc, checked, items.length)}
                  onKeyDown={event => {
                    if (rowDisabled) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleItem(group, qc, checked, items.length);
                    }
                  }}
                >
                  <span className={'menu-list-item-label'}>{item.name}</span>
                  <Toggle checked={checked} disabled={rowDisabled} />
                </div>
              );
            })}
          </CollapsibleMenuRow>
        );
      })}
    </>
  );
}
